import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { asDownload, fileKindLabel, fileMedia, fileSize, fileUrl, filenameCaption, type FileMedia } from '../lib/files.js'
import { Icon } from './Icon.js'
import { Spinner } from './Loader.js'
import { trapTab, useOverlay } from './overlay.js'
import { buttonClass } from './ui.js'

/** What the viewer needs to draw a file. `FeedFile` from the job card is one. */
export interface ViewerFile {
  id: string
  name: string
  /** Three or four letters off the filename: JPG, PDF, HEIC. */
  kind: string
  isImage: boolean
  meta: string
  /** Null until the bytes exist. Such a file is skipped, not drawn broken. */
  url: string | null
  /** Drawn under the picture when it says more than the filename does. */
  caption?: string
  /** Picks a player for a clip or a recording; a name alone is the fallback. */
  mimeType?: string
}

/** Image, clip, recording, PDF or something with no preview. */
export function viewerMedia(file: ViewerFile): FileMedia {
  if (file.isImage) return 'image'
  return fileMedia({ filename: file.name, mimeType: file.mimeType ?? '' })
}

const NOTHING: readonly ViewerFile[] = []

/**
 * The viewer and the one piece of state it needs, for a screen that opens
 * files from more than one list — the feed's rows, an email's attachments.
 * Render `viewer` once and call `open` with the list to step through.
 */
export function useFileViewer(): {
  open: (files: readonly ViewerFile[], id: string) => void
  viewer: ReactNode
} {
  const [state, setState] = useState<{ files: readonly ViewerFile[]; id: string } | null>(null)
  const open = useCallback((files: readonly ViewerFile[], id: string) => setState({ files, id }), [])
  const close = useCallback(() => setState(null), [])

  return {
    open,
    viewer: <FileViewer files={state?.files ?? NOTHING} openId={state?.id ?? null} onClose={close} />,
  }
}

/**
 * A file opened where you are, before any new tab.
 *
 * A photo fills the screen on a dark field. The arrows, the arrow keys, a
 * swipe or the strip along the bottom step through the rest; a double-click,
 * a double-tap, a pinch or the zoom button looks closer, and a drag moves
 * round a zoomed photo. A clip plays and a recording plays in place. A PDF is
 * drawn by the browser's own viewer in a frame. Anything else says there is
 * no preview. Every file keeps **Open in new tab** and **Download**, because
 * the viewer is the first step and not the only one.
 *
 * Its own overlay rather than `Modal`: the photo wants the whole screen, not
 * a white card. It stacks through `useOverlay`, so Escape closes the viewer
 * and leaves the job card underneath open — and on an installed app, where a
 * new tab leaves the app altogether, this is the difference between looking
 * at a photo and losing your place.
 */
export function FileViewer({
  files,
  openId,
  onClose,
}: {
  files: readonly ViewerFile[]
  /** The file to show, or null for a closed viewer. */
  openId: string | null
  onClose: () => void
}) {
  const viewable = files.filter((file) => file.url)
  // Held by id rather than position, so a refetch that adds a photo does not
  // swap the one on screen for its neighbour.
  const [currentId, setCurrentId] = useState(openId)
  useEffect(() => setCurrentId(openId), [openId])

  const found = viewable.findIndex((file) => file.id === currentId)
  const index = found === -1 ? viewable.findIndex((file) => file.id === openId) : found
  const open = openId !== null && index !== -1
  const depth = useOverlay(open, onClose)
  const panel = useRef<HTMLDivElement>(null)
  const [zoomed, setZoomed] = useState(false)
  const zoomToggle = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (open) panel.current?.focus()
  }, [open])

  const count = viewable.length
  const file = open ? viewable[index] : undefined
  const step = (by: number) => {
    if (count > 1) setCurrentId(viewable[(index + by + count) % count]!.id)
  }

  // The neighbours, fetched while this one is looked at, so the next click is
  // not a wait on a photo coming through the API. Pictures only: a clip or a
  // PDF fetched as an image is the whole file downloaded for nothing.
  const neighbours =
    count > 1
      ? [viewable[(index + 1) % count], viewable[(index - 1 + count) % count]]
          .filter((entry): entry is ViewerFile => Boolean(entry?.isImage && entry.url))
          .map((entry) => entry.url!)
      : []
  const preload = neighbours.join(' ')
  useEffect(() => {
    if (!preload) return
    for (const url of preload.split(' ')) new Image().src = url
  }, [preload])

  if (!open || !file?.url) return null

  const media = viewerMedia(file)
  const many = count > 1
  const strip = viewable.filter((entry) => {
    const kind = viewerMedia(entry)
    return kind === 'image' || kind === 'video'
  })
  // A caption someone wrote, not the filename again without its extension.
  const caption =
    file.caption && file.caption !== file.name && file.caption !== filenameCaption(file.name) ? file.caption : null

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      trapTab(event, event.currentTarget)
      return
    }
    // A player keeps its own arrows: they seek.
    const target = event.target as HTMLElement
    if (target.closest('video, audio, input, textarea')) return
    const moves: Record<string, () => void> = {
      ArrowRight: () => step(1),
      ArrowLeft: () => step(-1),
      Home: () => setCurrentId(viewable[0]!.id),
      End: () => setCurrentId(viewable[count - 1]!.id),
    }
    const move = moves[event.key]
    if (!move || !many) return
    // The job card and the screens under it are this element's React parents,
    // and the arrows are the viewer's while it is open.
    event.stopPropagation()
    event.preventDefault()
    move()
  }

  const chrome = 'text-ink-100 hover:bg-ink-800 hover:text-white'

  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label={file.name}
      tabIndex={-1}
      className="fixed inset-0 flex flex-col bg-ink-900 pt-[var(--safe-top)] pb-[var(--safe-bottom)] text-white outline-none"
      style={{ zIndex: 50 + depth * 10 }}
      onKeyDown={onKeyDown}
    >
      <header className="flex shrink-0 items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-4">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{file.name}</p>
          <p className="truncate text-xs text-ink-300" aria-live="polite">
            {many ? `${index + 1} of ${count} · ` : ''}
            {file.meta}
          </p>
        </div>
        {media === 'image' ? (
          <button
            type="button"
            aria-label={zoomed ? 'Zoom out' : 'Zoom in'}
            aria-pressed={zoomed}
            onClick={() => zoomToggle.current?.()}
            className={buttonClass({ size: 'sm', variant: 'ghost', className: chrome })}
          >
            <Icon name={zoomed ? 'zoomOut' : 'zoomIn'} className="size-4" />
          </button>
        ) : null}
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer"
          aria-label="Open in new tab"
          className={buttonClass({ size: 'sm', variant: 'ghost', className: chrome })}
        >
          <Icon name="external" className="size-4" />
          <span className="hidden sm:inline">Open in new tab</span>
        </a>
        <a
          href={asDownload(file.url)}
          aria-label="Download"
          className={buttonClass({ size: 'sm', variant: 'ghost', className: chrome })}
        >
          <Icon name="download" className="size-4" />
          <span className="hidden sm:inline">Download</span>
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="grid size-9 shrink-0 place-items-center rounded-md text-ink-100 hover:bg-ink-800 hover:text-white"
        >
          <Icon name="close" />
        </button>
      </header>

      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-2 pb-2 sm:px-16">
        {media === 'image' ? (
          <ImageStage
            key={file.id}
            file={file}
            onStep={many ? step : undefined}
            onZoomChange={setZoomed}
            toggleRef={zoomToggle}
          />
        ) : media === 'video' ? (
          <video
            key={file.id}
            src={file.url}
            controls
            playsInline
            preload="metadata"
            className="max-h-full max-w-full rounded-md bg-black"
          />
        ) : media === 'audio' ? (
          <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-lg bg-ink-800 p-6 text-center">
            <Icon name="mic" className="size-8 text-brand-300" />
            <audio key={file.id} src={file.url} controls preload="metadata" className="w-full" />
          </div>
        ) : media === 'pdf' ? (
          <iframe key={file.id} src={file.url} title={file.name} className="size-full rounded-md bg-white" />
        ) : (
          <NoPreview file={file} />
        )}

        {many ? (
          <>
            <NavButton side="left" label="Previous" onClick={() => step(-1)} />
            <NavButton side="right" label="Next" onClick={() => step(1)} />
          </>
        ) : null}
      </div>

      {caption ? (
        <p className="shrink-0 px-4 pb-2 text-center text-sm text-pretty text-ink-100">{caption}</p>
      ) : null}

      {(media === 'image' || media === 'video') && strip.length > 1 ? (
        <Strip files={strip} currentId={file.id} onPick={setCurrentId} />
      ) : null}
    </div>,
    document.body,
  )
}

/* -------------------------------------------------------------------------- */
/* A photo: loading, zoom and pan, and the swipe to the next one              */
/* -------------------------------------------------------------------------- */

const MAX_SCALE = 5
const ZOOM_STEP = 2.5
/** A horizontal drag past this on a photo at its normal size is a swipe. */
const SWIPE_PX = 60
/** Two taps this close together, in time and place, are a double-tap. */
const DOUBLE_TAP_MS = 300
const DOUBLE_TAP_PX = 30

interface View {
  scale: number
  x: number
  y: number
}

const AT_REST: View = { scale: 1, x: 0, y: 0 }

/**
 * One photo. Keyed by file, so a new photo arrives unzoomed and unloaded.
 *
 * The zoom is a transform on a picture laid out at its fitted size, so the
 * arithmetic stays in that one coordinate space: a point under the finger
 * stays under it as the scale changes, and the pan is held to the picture's
 * own edges.
 */
function ImageStage({
  file,
  onStep,
  onZoomChange,
  toggleRef,
}: {
  file: ViewerFile
  /** Absent when there is only the one photo. */
  onStep: ((by: number) => void) | undefined
  onZoomChange: (zoomed: boolean) => void
  toggleRef: { current: (() => void) | null }
}) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [view, setView] = useState<View>(AT_REST)
  const [swipe, setSwipe] = useState(0)
  const [moving, setMoving] = useState(false)
  const stage = useRef<HTMLDivElement>(null)
  const image = useRef<HTMLImageElement>(null)
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const gesture = useRef<{
    start: View
    from: { x: number; y: number }
    distance: number
    moved: boolean
    /** Began on the photo rather than the dark round it. */
    onPhoto: boolean
  } | null>(null)
  const lastTap = useRef<{ at: number; x: number; y: number } | null>(null)

  const zoomed = view.scale > 1.01
  useEffect(() => onZoomChange(zoomed), [zoomed, onZoomChange])

  /** Keeps the picture's edge from leaving its side of the stage. */
  const clamp = useCallback((next: View): View => {
    const scale = Math.min(MAX_SCALE, Math.max(1, next.scale))
    const width = image.current?.offsetWidth ?? 0
    const height = image.current?.offsetHeight ?? 0
    const reachX = ((scale - 1) * width) / 2
    const reachY = ((scale - 1) * height) / 2
    return {
      scale,
      x: Math.min(reachX, Math.max(-reachX, next.x)),
      y: Math.min(reachY, Math.max(-reachY, next.y)),
    }
  }, [])

  /** The point on screen, measured from the middle of the stage. */
  const fromCentre = useCallback((clientX: number, clientY: number) => {
    const box = stage.current?.getBoundingClientRect()
    if (!box) return { x: 0, y: 0 }
    return { x: clientX - (box.left + box.width / 2), y: clientY - (box.top + box.height / 2) }
  }, [])

  /** Scales about a point, so what is under it stays under it. */
  const zoomAt = useCallback(
    (from: View, scale: number, point: { x: number; y: number }): View =>
      clamp({
        scale,
        x: point.x - (scale / from.scale) * (point.x - from.x),
        y: point.y - (scale / from.scale) * (point.y - from.y),
      }),
    [clamp],
  )

  const toggle = useCallback(
    (point = { x: 0, y: 0 }) =>
      setView((current) => (current.scale > 1.01 ? AT_REST : zoomAt(current, ZOOM_STEP, point))),
    [zoomAt],
  )

  useEffect(() => {
    toggleRef.current = () => toggle()
    return () => {
      toggleRef.current = null
    }
  }, [toggle, toggleRef])

  // A trackpad pinch arrives as a wheel with Ctrl held. Not passive, because
  // the page would zoom too.
  useEffect(() => {
    const element = stage.current
    if (!element) return
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      const point = fromCentre(event.clientX, event.clientY)
      setView((current) => zoomAt(current, current.scale * Math.exp(-event.deltaY / 200), point))
    }
    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [fromCentre, zoomAt])

  const down = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const [a, b] = [...pointers.current.values()]
    gesture.current = {
      start: view,
      from: b ? { x: (a!.x + b.x) / 2, y: (a!.y + b.y) / 2 } : { x: event.clientX, y: event.clientY },
      distance: b ? Math.hypot(a!.x - b.x, a!.y - b.y) : 0,
      moved: false,
      // Read here: once the stage holds the pointer, the release is aimed at
      // the stage whatever it was over.
      onPhoto: event.target === image.current,
    }
    setMoving(true)
  }

  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId) || !gesture.current) return
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const { start, from, distance } = gesture.current
    const [a, b] = [...pointers.current.values()]

    if (b && distance > 0) {
      const middle = { x: (a!.x + b.x) / 2, y: (a!.y + b.y) / 2 }
      const scale = start.scale * (Math.hypot(a!.x - b.x, a!.y - b.y) / distance)
      const zoomedView = zoomAt(start, scale, fromCentre(from.x, from.y))
      gesture.current.moved = true
      setView(clamp({ ...zoomedView, x: zoomedView.x + middle.x - from.x, y: zoomedView.y + middle.y - from.y }))
      return
    }

    const dx = event.clientX - from.x
    const dy = event.clientY - from.y
    if (Math.abs(dx) + Math.abs(dy) > 4) gesture.current.moved = true
    if (start.scale > 1.01) setView(clamp({ ...start, x: start.x + dx, y: start.y + dy }))
    // A mouse steps with the arrows; a finger drags the photo along.
    else if (onStep && event.pointerType !== 'mouse') setSwipe(dx)
  }

  const up = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.delete(event.pointerId)) return
    const current = gesture.current
    if (pointers.current.size > 0) {
      // One finger of a pinch lifted: carry on as a pan from where it is.
      const [rest] = [...pointers.current.values()]
      gesture.current = { start: view, from: rest!, distance: 0, moved: true, onPhoto: false }
      return
    }
    gesture.current = null
    setMoving(false)

    if (swipe !== 0) {
      if (Math.abs(swipe) > SWIPE_PX) onStep?.(swipe < 0 ? 1 : -1)
      setSwipe(0)
      return
    }

    if (!current || current.moved) return

    // A click on the photo looks closer, and another steps back.
    if (event.pointerType === 'mouse') {
      if (current.onPhoto) toggle(fromCentre(event.clientX, event.clientY))
      return
    }

    // A finger double-taps, because one tap is how it reaches for the arrows.
    {
      const tap = { at: event.timeStamp, x: event.clientX, y: event.clientY }
      const last = lastTap.current
      if (last && tap.at - last.at < DOUBLE_TAP_MS && Math.hypot(tap.x - last.x, tap.y - last.y) < DOUBLE_TAP_PX) {
        lastTap.current = null
        toggle(fromCentre(tap.x, tap.y))
      } else {
        lastTap.current = tap
      }
    }
  }

  return (
    <div
      ref={stage}
      className={cn(
        'relative flex size-full touch-none items-center justify-center select-none',
        zoomed ? (moving ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in',
      )}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      {status === 'loading' ? (
        <span className="absolute inset-0 grid place-items-center text-ink-300" role="status" aria-label="Loading the photo">
          <Spinner className="size-8" />
        </span>
      ) : null}
      {status === 'failed' ? (
        <NoPreview file={file} reason="This photo could not be shown here." />
      ) : (
        <img
          ref={image}
          src={file.url!}
          alt={file.caption ?? file.name}
          draggable={false}
          onLoad={() => setStatus('ready')}
          onError={() => setStatus('failed')}
          style={{
            transform: `translate(${view.x + swipe}px, ${view.y}px) scale(${view.scale})`,
            transition: moving ? 'none' : 'transform 160ms ease-out, opacity 160ms ease-out',
            opacity: status === 'ready' ? 1 - Math.min(Math.abs(swipe) / 600, 0.4) : 0,
          }}
          className="max-h-full max-w-full rounded-md object-contain will-change-transform"
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The pieces around it                                                       */
/* -------------------------------------------------------------------------- */

function NoPreview({ file, reason }: { file: ViewerFile; reason?: string }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 text-center">
      <p className="text-sm text-ink-200">{reason ?? `There is no preview for a ${file.kind} file.`}</p>
      <a href={asDownload(file.url!)} className={buttonClass({ variant: 'primary' })}>
        Download it
      </a>
    </div>
  )
}

/** The photos and clips along the bottom, the current one kept in view. */
function Strip({
  files,
  currentId,
  onPick,
}: {
  files: readonly ViewerFile[]
  currentId: string
  onPick: (id: string) => void
}) {
  const current = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    current.current?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' })
  }, [currentId])

  return (
    <div className="flex shrink-0 gap-1.5 overflow-x-auto px-4 pb-3 [scrollbar-width:none] sm:justify-center">
      {files.map((entry) => {
        const here = entry.id === currentId
        return (
          <button
            key={entry.id}
            ref={here ? current : undefined}
            type="button"
            aria-label={`Show ${entry.name}`}
            aria-current={here ? 'true' : undefined}
            onClick={() => onPick(entry.id)}
            className={cn(
              'relative size-12 shrink-0 overflow-hidden rounded-md bg-ink-800 ring-2 transition-opacity',
              here ? 'ring-white' : 'opacity-50 ring-transparent hover:opacity-100',
            )}
          >
            {entry.isImage ? (
              <img src={entry.url!} alt="" loading="lazy" decoding="async" className="size-full object-cover" />
            ) : (
              <span className="grid size-full place-items-center text-white">
                <Icon name="play" className="size-4" />
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

function NavButton({ side, label, onClick }: { side: 'left' | 'right'; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-10 grid size-11 -translate-y-1/2 place-items-center rounded-md bg-ink-800 text-white ring-1 ring-ink-700 hover:bg-ink-700',
        side === 'left' ? 'left-2 sm:left-4' : 'right-2 sm:right-4',
      )}
    >
      <Icon name={side === 'left' ? 'back' : 'forward'} />
    </button>
  )
}

/**
 * An attachment row as the viewer reads it. Every record with bytes has these
 * columns — a job's file, an email's attachment, a photographed licence card.
 */
export function attachmentViewerFile(
  attachment: { id: string; filename: string; mimeType: string; checksum: string | null; sizeBytes?: number | null; kind?: string | null },
  meta?: string,
): ViewerFile {
  const kind = fileKindLabel(attachment.filename, attachment.mimeType)
  return {
    id: attachment.id,
    name: attachment.filename,
    kind,
    isImage: fileMedia(attachment) === 'image',
    mimeType: attachment.mimeType,
    meta: meta ?? [kind, fileSize(attachment.sizeBytes)].filter(Boolean).join(' · '),
    url: fileUrl(attachment),
  }
}

/**
 * A link to a file that opens the viewer on a plain click.
 *
 * Still an anchor with the file's address, because the office middle-clicks
 * a PDF to read the figures beside the screen while it rings somebody — a
 * modifier or the middle button goes to a new tab as it always did. Only the
 * ordinary click stays in the app, which on an installed iPad is the
 * difference between reading the invoice and leaving the app to do it.
 */
export function FileLink({
  file,
  onOpen,
  className,
  children,
}: {
  file: ViewerFile
  onOpen: (file: ViewerFile) => void
  className?: string
  children: ReactNode
}) {
  if (!file.url) return null
  return (
    <a
      href={file.url}
      target="_blank"
      rel="noreferrer"
      className={className}
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        event.preventDefault()
        onOpen(file)
      }}
    >
      {children}
    </a>
  )
}

/**
 * A message's or a record's files: pictures as tiles, the rest as a line
 * each, every one opening the viewer. For a list of files that is not a
 * whole tab of them, which is `FilesPane`.
 */
export function FileGallery({
  files,
  onOpen,
  className,
}: {
  files: readonly ViewerFile[]
  onOpen: (file: ViewerFile) => void
  className?: string
}) {
  const pictures = files.filter((file) => {
    const media = viewerMedia(file)
    return media === 'image' || media === 'video'
  })
  const rest = files.filter((file) => !pictures.includes(file))

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {pictures.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-1.5">
          {pictures.map((file) => (
            <FileThumb key={file.id} file={file} onOpen={onOpen} className="aspect-square w-full" />
          ))}
        </div>
      ) : null}
      {rest.map((file) => (
        <button
          key={file.id}
          type="button"
          disabled={!file.url}
          onClick={() => onOpen(file)}
          title={file.url ? undefined : 'Not fetched yet'}
          className="flex w-full items-center gap-2.5 rounded-md bg-white px-2.5 py-2 text-left ring-1 ring-ink-200 ring-inset transition-colors enabled:hover:bg-ink-50 disabled:opacity-60"
        >
          <span className="w-9 shrink-0 rounded-sm bg-ink-100 py-0.5 text-center text-[10px] font-bold text-ink-700">
            {file.kind}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-ink-900">{file.name}</span>
            <span className="block truncate text-xs text-ink-500">{file.meta}</span>
          </span>
        </button>
      ))}
    </div>
  )
}

/**
 * A photo or clip in a grid or a row, opening the viewer on a click.
 *
 * One tile for every list of pictures so they load the same way — lazily,
 * because a job can carry sixty photographs and each is a request through the
 * API rather than a CDN — and say the same thing when the bytes are not here
 * yet, rather than drawing a broken image.
 */
export function FileThumb({
  file,
  onOpen,
  className,
}: {
  file: ViewerFile
  onOpen: (file: ViewerFile) => void
  /** Sizes the tile: an aspect ratio, a height, a width. */
  className?: string
}) {
  const media = viewerMedia(file)
  const tile = cn('relative block overflow-hidden rounded-md bg-ink-100', className)

  if (!file.url) {
    return (
      <span className={cn(tile, 'flex flex-col justify-end p-1.5')} title={file.meta}>
        <span className="truncate text-[10px] font-medium text-ink-600">{file.name}</span>
      </span>
    )
  }

  return (
    <button
      type="button"
      aria-label={`View ${file.name}`}
      title={file.meta}
      onClick={() => onOpen(file)}
      className={cn(tile, 'focus-visible:ring-2 focus-visible:ring-ink-900 focus-visible:outline-none')}
    >
      {media === 'image' ? (
        <img src={file.url} alt={file.caption ?? file.name} loading="lazy" decoding="async" className="size-full object-cover" />
      ) : (
        <span className="grid size-full place-items-center bg-ink-800 text-white">
          <Icon name={media === 'video' ? 'play' : 'paperclip'} className="size-5" />
          {media === 'video' ? null : (
            <span className="absolute inset-x-1 bottom-1 truncate text-[10px] font-semibold">{file.kind}</span>
          )}
        </span>
      )}
    </button>
  )
}